"use client";

import { motion } from "motion/react";
import { ChevronDown } from "lucide-react";
import { useState, useEffect } from "react";
import Image from "next/image";

export function HeroSection({ homeDetails }: any) {
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const bannerLength = homeDetails?.Banner?.length || 1; // Add this line

  useEffect(() => {
    if (bannerLength <= 1) return; // Don't run interval if only one or no banner

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % bannerLength);
    }, 5000);
    return () => clearInterval(interval);
  }, [bannerLength]); // Add bannerLength as dependency

  const goToSlide = (index: number) => {
    setCurrentIndex(index);
  };

  const goToPrevious = () => {
    if (bannerLength <= 1) return; // Guard clause

    setCurrentIndex((prev) => {
      const newIndex = prev - 1;
      return newIndex < 0 ? bannerLength - 1 : newIndex;
    });
  };

  const goToNext = () => {
    if (bannerLength <= 1) return; // Guard clause

    setCurrentIndex((prev) => (prev + 1) % bannerLength);
  };

  return (
    <section className="relative min-h-screen flex items-end justify-start overflow-hidden">
      {/* Carousel Images */}
      {homeDetails?.Banner?.map((image: any, index: number) => {
        return (
          <motion.div
            key={index}
            className="absolute inset-0 z-0"
            initial={{ opacity: 0 }}
            animate={{ opacity: index === currentIndex ? 1 : 0 }}
            transition={{ duration: 1 }}
          >
            <Image
              src={image?.BannerImage}
              alt={image?.BannerTitle || "Hero Image"}
              className="object-cover"
              fill
              priority={index === 0}
              loading={index === 0 ? "eager" : "lazy"}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 100vw, 1430px"
              quality={75}
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/0 via-black/0 to-black/60 z-10" />
          </motion.div>
        );
      })}

      {/* Carousel Navigation */}
      {/* {bannerLength > 1 && (
        <>
          <button
            onClick={goToPrevious}
            className="absolute left-4 md:left-8 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Previous slide"
          >
            <ChevronLeft className="h-6 w-6 text-white" />
          </button>

          <button
            onClick={goToNext}
            className="absolute right-4 md:right-8 z-20 w-12 h-12 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 flex items-center justify-center hover:bg-white/20 transition-colors"
            aria-label="Next slide"
          >
            <ChevronRight className="h-6 w-6 text-white" />
          </button>
        </>
      )} */}

      {/* Carousel Indicators */}
      {/* <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 flex gap-3">
        {homeDetails?.Banner?.map((_: any, index: number) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className={`w-3 h-3 rounded-full transition-all duration-300 ${
              index === currentIndex
                ? "bg-[#C9A962] w-8"
                : "bg-white/40 hover:bg-white/60"
            }`}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div> */}

      {/* Decorative Elements */}
      <div className="absolute inset-0 z-0">
        <div className="absolute top-0 left-0 w-full h-32 bg-gradient-to-b from-black/30 to-transparent" />
        <div className="absolute bottom-0 left-0 w-full h-32 bg-gradient-to-t from-black/50 to-transparent" />
      </div>

      {/* Content */}
      <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8 text-center mt-25 mb-15">
        {/* Premium Badge */}
        {homeDetails?.Banner &&
          homeDetails?.Banner?.[currentIndex] &&
          homeDetails?.Banner?.[currentIndex]?.SubTitle && (
            <motion.div
              className="inline-block mb-6"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.5 }}
            >
              <div className="bg-white/10 backdrop-blur-md border border-white/20 px-8 py-3 rounded-full">
                <p className="text-[#C9A962] tracking-[0.4em] text-sm">
                  {homeDetails?.Banner?.[currentIndex]?.SubTitle}
                </p>
              </div>
            </motion.div>
          )}

        <h1 className="text-white text-3xl md:text-4xl lg:text-5xl mb-8 max-w-6xl mx-auto leading-[1.1] tracking-tight font-playfair">
          <span className="text-[#C9A962]">
            {homeDetails?.Banner?.[currentIndex]?.BannerTitle}
          </span>
        </h1>

        <motion.div
          className="w-24 h-px bg-[#C9A962] mx-auto mb-8"
          initial={{ width: 0 }}
          animate={{ width: 96 }}
          transition={{ duration: 0.8, delay: 0.9 }}
        />

        <motion.div
          className="text-white/95 text-xl md:text-2xl max-w-4xl mx-auto mb-12 leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
          suppressHydrationWarning={true}
          dangerouslySetInnerHTML={{
            __html: homeDetails?.Banner?.[currentIndex]?.Description?.trim(),
          }}
        />

        {/* {homeDetails?.Banner?.[currentIndex]?.URL && (
            <motion.div
              className="flex flex-col sm:flex-row gap-6 justify-center"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.2 }}
            >
              <Link
                href={
                  homeDetails?.Banner?.[currentIndex]?.URL
                    ? `${homeDetails?.Banner?.[currentIndex]?.URL || "#"}`
                    : ""
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                <Button
                  size="lg"
                  type="button"
                  className="bg-[#B42025] hover:bg-[#8F1A1E] text-white px-10 py-7 text-lg shadow-2xl hover:shadow-[#B42025]/50 transition-all duration-300"
                >
                  Explore Our Resorts
                </Button>
              </Link>
            </motion.div>
          )} */}
      </div>

      {/* Scroll Indicator */}
      <motion.div
        className="absolute bottom-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 cursor-pointer"
        animate={{ y: [0, 12, 0] }}
        transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        onClick={() => {
          window.scrollBy({
            top: window.innerHeight,
            behavior: "smooth",
          });
        }}
      >
        <p className="text-white/70 text-sm tracking-widest">
          SCROLL TO EXPLORE
        </p>
        <ChevronDown className="h-6 w-6 text-white/70" />
      </motion.div>
    </section>
  );
}
